import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import { timeAgo } from "../lib/time";
import { toast } from "sonner";
import { ChevronLeft, User, Mail, MessageSquare, Paperclip, Clock, UserCheck } from "lucide-react";
import { formatBytes, isImage, attachmentUrl } from "../lib/attachments";
import { EmailBody } from "../components/EmailBody";
import { useAuth } from "../contexts/AuthContext";

export default function MyTicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/tickets/mine/${id}`)
      .then((r) => setTicket(r.data))
      .catch((err) => {
        toast.error("Could not load ticket", { description: formatApiError(err) });
        nav("/my-tickets");
      })
      .finally(() => setLoading(false));
  }, [id, nav]);

  if (loading) {
    return <div className="p-10 text-center text-sm text-slate-500">Loading ticket...</div>;
  }
  if (!ticket) return null;

  // Build a chronological conversation: original message + all replies
  const conversation = [
    {
      kind: "customer",
      author: ticket.customer_name,
      created_at: ticket.created_at,
      body: ticket.message,
      body_html: ticket.message_html || "",
      attachments: ticket.attachments || [],
    },
    ...(ticket.replies || []).map((r) => ({
      kind: "support",
      id: r.id,
      author: r.author_name,
      created_at: r.created_at,
      body: r.body,
      body_html: r.body_html || "",
      attachments: r.attachments || [],
    })),
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
      <Link to="/my-tickets" data-testid="back-to-my-tickets" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ChevronLeft className="w-4 h-4" /> Back to my tickets
      </Link>

      <div className="beacon-card p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                {ticket.ticket_number}
              </span>
              <span className="text-[11px] text-slate-400">·</span>
              <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {timeAgo(ticket.created_at)}
              </span>
            </div>
            <h1 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900 font-display">{ticket.subject}</h1>
            <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
              <span className="capitalize">{ticket.category.replace("_", " ")}</span>
              <span className="inline-flex items-center gap-1">
                <UserCheck className="w-3.5 h-3.5" />
                {ticket.assignee_name || <span className="italic text-slate-400">not yet assigned</span>}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="ticket-conversation">
        {conversation.map((msg, idx) => {
          const mine = msg.kind === "customer";
          return (
            <div
              key={idx}
              className={`beacon-card p-4 sm:p-5 ${
                mine ? "border-slate-200" : "border-blue-100 bg-blue-50/40"
              }`}
              data-testid={`conversation-msg-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${
                    mine ? "bg-slate-900 text-white" : "bg-blue-600 text-white"
                  }`}>
                    {(msg.author || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-slate-900">
                      {mine ? "You" : msg.author}
                      <span className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        mine ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-blue-700"
                      }`}>
                        {mine ? "customer" : "support"}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500">{new Date(msg.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <EmailBody text={msg.body} html={msg.body_html} />
              </div>

              {(msg.attachments || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-2">
                    <Paperclip className="w-3.5 h-3.5" /> Attachments ({msg.attachments.length})
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {msg.attachments.map((a) => {
                      const url = attachmentUrl(a, { email: ticket.customer_email });
                      const image = isImage(a);
                      return (
                        <a
                          key={a.id}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-sm transition-all"
                          data-testid={`msg-attachment-${a.id}`}
                        >
                          {image ? (
                            <div className="aspect-video bg-slate-50 overflow-hidden">
                              <img src={url} alt={a.filename} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                          ) : (
                            <div className="aspect-video bg-slate-50 grid place-items-center">
                              <Paperclip className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                          <div className="p-2">
                            <div className="text-xs font-semibold text-slate-800 truncate">{a.filename}</div>
                            <div className="text-[10px] text-slate-500">{formatBytes(a.size)}</div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {(ticket.replies || []).length === 0 && (
          <div className="beacon-card p-5 border-dashed border-slate-200 text-center text-sm text-slate-500">
            No replies from support yet. We'll email you the moment there's an update.
          </div>
        )}
      </div>
    </div>
  );
}
